"use client";

import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
    Shield, CreditCard, ClipboardCheck, Truck, Wrench, Car,
    Store, Navigation as NavIcon, Search, MapPin,
    ExternalLink, PhoneCall, Star, Loader2, AlertCircle,
    MessageCircle, MessageSquare, Route, CheckCircle2,
    Grid3X3, ArrowRight
} from 'lucide-react';
import { servicesApi, AutomotiveService } from '@/lib/api/services';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { buildWhatsAppUrl } from '@/lib/whatsapp';
import { useCountry } from '@/context/CountryContext';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';

const SERVICE_TYPES = [
    { id: '', label: 'كل الخدمات', name: 'All Services', icon: Store, image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?q=80&w=900&auto=format&fit=crop' },
    { id: 'insurance', label: 'تأمين', name: 'Insurance', icon: Shield, image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=900&auto=format&fit=crop' },
    { id: 'financing', label: 'تمويل', name: 'Financing', icon: CreditCard, image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?q=80&w=900&auto=format&fit=crop' },
    { id: 'rental', label: 'تأجير سيارات', name: 'Car Rental', icon: Car, image: 'https://images.unsplash.com/photo-1549924231-f129b911e442?q=80&w=900&auto=format&fit=crop' },
    { id: 'inspection', label: 'فحص', name: 'Inspection', icon: ClipboardCheck, image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=900&auto=format&fit=crop' },
    { id: 'towing', label: 'سحب (ونش)', name: 'Towing', icon: Truck, image: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?q=80&w=900&auto=format&fit=crop' },
    { id: 'mobile_workshop', label: 'ورشة متنقلة', name: 'Mobile Workshop', icon: Wrench, image: 'https://images.unsplash.com/photo-1615906655593-ad0386982a0f?q=80&w=900&auto=format&fit=crop' },
    { id: 'roadside', label: 'مساعدة على الطريق', name: 'Roadside Assistance', icon: NavIcon, image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=900&auto=format&fit=crop' },
    { id: 'dealership', label: 'معارض سيارات', name: 'Dealerships', icon: Store, image: 'https://images.unsplash.com/photo-1562141961-b5d08d8515e5?q=80&w=900&auto=format&fit=crop' },
];

const LOCATION_SERVICE_TYPES = ['towing', 'mobile_workshop', 'roadside'];

function parseCoordinate(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function getServiceCardImage(service: AutomotiveService): string | undefined {
    if (Array.isArray(service.gallery_images) && service.gallery_images.length > 0) {
        return service.gallery_images[0]?.image;
    }
    return service.logo;
}

export default function ServicesPage() {
    const router = useRouter();
    const { t } = useLanguage();
    const [services, setServices] = useState<AutomotiveService[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [browseStep, setBrowseStep] = useState<'categories' | 'items'>('categories');

    // Geolocation state
    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');

    // Assistance Request Modal
    const [selectedService, setSelectedService] = useState<AutomotiveService | null>(null);
    const [requestNotes, setRequestNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { toast } = useToast();
    const { selectedCountryCode } = useCountry();

    const fetchServices = async () => {
        setLoading(true);
        try {
            const filters: any = {};
            if (filterType) filters.service_type = filterType;
            if (searchQuery) filters.search = searchQuery;
            // Keep integrated services visible even when backend pagination is enabled.
            filters.page_size = 100;

            // If we have location, request sorted by distance
            if (userLocation) {
                filters.lat = userLocation.lat;
                filters.lng = userLocation.lng;
                filters.ordering = 'distance';
            }

            const data = await servicesApi.getServices(filters);
            const rows = Array.isArray((data as any)?.results)
                ? (data as any).results
                : Array.isArray((data as any)?.data?.results)
                    ? (data as any).data.results
                    : Array.isArray(data)
                        ? data
                        : [];

            setServices(rows as AutomotiveService[]);
        } catch (error) {
            toast({ title: 'خطأ', description: 'فشل في تحميل الخدمات', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, [filterType, searchQuery, userLocation]);

    useEffect(() => {
        const query = new URLSearchParams(window.location.search).get('search');
        if (query?.trim()) {
            setSearchQuery(query.trim());
            setBrowseStep('items');
        }
    }, []);

    const selectedServiceType = SERVICE_TYPES.find(type => type.id === filterType) || SERVICE_TYPES[0];

    const handleServiceTypeSelect = (type: typeof SERVICE_TYPES[number]) => {
        setFilterType(type.id);
        setBrowseStep('items');
    };

    const backToCategories = () => {
        setFilterType('');
        setSearchQuery('');
        setBrowseStep('categories');
    };

    const requestLocation = () => {
        setLocating(true);
        setLocationError('');
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setBrowseStep('items');
                    setLocating(false);
                    toast({ title: 'تم تحديد موقعك', description: 'يتم الآن عرض أقرب الخدمات لك' });
                },
                (error) => {
                    setLocating(false);
                    setLocationError('تعذر تحديد موقعك. يرجى تفعيل إذن الوصول للموقع.');
                    toast({ title: 'تنبيه', description: 'تعذر تحديد الموقع', variant: 'destructive' });
                }
            );
        } else {
            setLocating(false);
            setLocationError('المتصفح لا يدعم تحديد الموقع');
        }
    };

    const handleRequestAssistance = async () => {
        if (!selectedService) return;

        setSubmitting(true);
        try {
            await servicesApi.createRequest({
                service: selectedService.id,
                user_latitude: userLocation?.lat,
                user_longitude: userLocation?.lng,
                notes: requestNotes
            });

            toast({
                title: '✅ تم إرسال طلبك بنجاح',
                description: 'سيقوم مقدم الخدمة بالتواصل معك قريباً'
            });
            setSelectedService(null);
            setRequestNotes('');
        } catch (error) {
            toast({ title: 'خطأ', description: 'فشل إرسال الطلب', variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans" dir="rtl">
            <Navigation title="الخدمات المتكاملة" />

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white py-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 mix-blend-overlay"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <Badge className="bg-blue-500/20 text-blue-200 border-none mb-4 backdrop-blur-md">{t('integrated_services')}</Badge>
                    <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">دليلك الشامل لخدمات السيارات</h1>
                    <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                        تأمين، تمويل، ورش متنقلة، ومساعدة على الطريق. اكتشف أقرب مزودي الخدمات المعتمدين واطلب المساعدة بضغطة زر.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto">
                        <div className="relative w-full">
                            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <Input
                                placeholder="ابحث عن شركات التأمين، الورش، خدمات السحب..."
                                className="w-full h-14 pr-12 rounded-full bg-white/10 text-white border-white/20 placeholder:text-slate-400 focus-visible:ring-blue-500"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    if (e.target.value.trim()) setBrowseStep('items');
                                }}
                            />
                        </div>
                        <Button
                            onClick={requestLocation}
                            disabled={locating}
                            className={`h-14 rounded-full px-6 gap-2 whitespace-nowrap transition-all ${userLocation ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-primary hover:bg-primary/90'}`}
                        >
                            {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />}
                            {userLocation ? 'موقعك محدّد' : 'استخدام موقعي الحالي'}
                        </Button>
                    </div>
                    {locationError && <p className="text-amber-400 text-sm mt-3 flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" /> {locationError}</p>}
                </div>
            </div>

            <div className="flex-grow max-w-7xl mx-auto w-full px-4 py-8">

                {browseStep === 'items' && (
                    <div className="space-y-3 rounded-[1.5rem] bg-white p-3 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                            <Badge className="rounded-full bg-primary px-4 py-2 text-white">
                                {selectedServiceType.label}
                            </Badge>
                            <Button variant="outline" className="rounded-2xl shrink-0 gap-2" onClick={backToCategories}>
                                <ArrowRight className="h-4 w-4" />
                                التصنيفات
                            </Button>
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {SERVICE_TYPES.map(type => {
                                const TypeIcon = type.icon;
                                return (
                                    <button
                                        key={type.id || 'all'}
                                        onClick={() => handleServiceTypeSelect(type)}
                                        className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors flex items-center gap-2 ${
                                            filterType === type.id ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                                        }`}
                                    >
                                        <TypeIcon className="h-3.5 w-3.5" />
                                        {type.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {browseStep === 'categories' && (
                    <div className="space-y-4">
                        <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
                            <Grid3X3 className="w-6 h-6 text-primary" />
                            تصفح حسب التصنيف
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {SERVICE_TYPES.map(type => {
                                const TypeIcon = type.icon;
                                return (
                                    <button key={type.id || 'all'} onClick={() => handleServiceTypeSelect(type)} className="group text-right">
                                        <div className="relative h-36 overflow-hidden rounded-[1.5rem] bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-xl">
                                            <img src={type.image} alt={type.label} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />
                                            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/20 to-transparent" />
                                            <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-white/95 text-primary shadow-sm backdrop-blur">
                                                <TypeIcon className="h-4 w-4" />
                                            </div>
                                            <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                                                <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white/20 text-white shadow-sm backdrop-blur-md ring-1 ring-white/25">
                                                    <TypeIcon className="h-3.5 w-3.5" />
                                                </div>
                                                <h3 className="truncate text-sm font-black sm:text-base">{type.label}</h3>
                                                <p className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{type.name}</p>
                                            </div>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between rounded-full bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-500">
                                            <span>عرض الخدمات</span>
                                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm">
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Services Grid */}
                {browseStep === 'items' && (
                <div className="mt-8">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader2 className="w-12 h-12 animate-spin text-primary" />
                        </div>
                    ) : services.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
                            <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                            <h2 className="text-xl font-bold text-slate-800">لا توجد خدمات متاحة</h2>
                            <p className="text-slate-500 mt-2">جرب البحث بكلمات أخرى أو اختر فئة مختلفة.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {services.map(service => {
                                const isLocationService = LOCATION_SERVICE_TYPES.includes(service.service_type);
                                const lat = parseCoordinate(service.latitude);
                                const lng = parseCoordinate(service.longitude);
                                const hasCoordinates = lat !== null && lng !== null;
                                const primaryImage = getServiceCardImage(service);
                                const displayAddress = service.address_ar || service.address;
                                const serviceArea = service.service_area_ar || service.service_area;
                                const highlights = Array.isArray(service.highlights) ? service.highlights.filter(Boolean).slice(0, 2) : [];
                                const mapsUrl = hasCoordinates
                                    ? `https://www.google.com/maps?q=${lat},${lng}`
                                    : displayAddress
                                        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`
                                        : null;
                                const whatsappUrl = service.contact_phone
                                    ? buildWhatsAppUrl(
                                        service.contact_phone,
                                        `السلام عليكم، أنا مهتم بخدمة "${service.name}" عبر تطبيق سيارتي 360. هل يمكن مشاركة التفاصيل المتاحة؟`,
                                        selectedCountryCode
                                    )
                                    : null;

                                return (
                                    <Card
                                        key={service.id}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`عرض تفاصيل ${service.name}`}
                                        onClick={() => router.push(`/services/${service.id}`)}
                                        onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                router.push(`/services/${service.id}`);
                                            }
                                        }}
                                        className="rounded-3xl border-slate-100 shadow-xl shadow-slate-200/50 hover:-translate-y-1 transition-transform overflow-hidden flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    >
                                        <CardContent className="p-0 flex flex-col h-full">
                                            <div className="p-6 bg-gradient-to-br from-white to-slate-50 border-b border-slate-100 relative">
                                                {service.is_featured && (
                                                    <div className="absolute top-4 left-4 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                                        <Star className="w-3 h-3 fill-amber-500" /> مميز
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 mb-4">
                                                    <div className="w-16 h-16 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center p-2 overflow-hidden">
                                                        {primaryImage ? (
                                                            <img src={primaryImage} alt={service.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Store className="w-8 h-8 text-primary/40" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-lg text-slate-800 line-clamp-1">{service.name}</h3>
                                                        <Badge variant="outline" className="mt-1 bg-white text-slate-500 border-slate-200">
                                                            {SERVICE_TYPES.find(t => t.id === service.service_type)?.label || service.service_type}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-600 line-clamp-2 min-h-[40px]">
                                                    {service.short_description_ar || service.short_description || 'مزود خدمة معتمد في المنصة.'}
                                                </p>
                                                {(service.price_label || service.service_duration || serviceArea || highlights.length > 0) && (
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        {service.price_label && (
                                                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100">
                                                                {service.price_label}
                                                            </Badge>
                                                        )}
                                                        {service.service_duration && (
                                                            <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-100">
                                                                {service.service_duration}
                                                            </Badge>
                                                        )}
                                                        {serviceArea && (
                                                            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-100 line-clamp-1 max-w-full">
                                                                {serviceArea}
                                                            </Badge>
                                                        )}
                                                        {highlights.map((tag, idx) => (
                                                            <Badge key={`${service.id}-tag-${idx}`} variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200">
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="p-6 bg-white flex flex-col flex-1 justify-between gap-4">
                                                <div className="space-y-3 text-sm flex-1">
                                                    {service.provider_store_name && (
                                                        <div className="flex items-center gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                                                                <Store className="w-4 h-4" />
                                                            </div>
                                                            <span className="font-medium line-clamp-1">{service.provider_store_name}</span>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center gap-3 text-slate-700">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${service.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                                                            <CheckCircle2 className="w-4 h-4" />
                                                        </div>
                                                        <span className={`font-medium ${service.is_active ? 'text-emerald-700' : 'text-slate-500'}`}>
                                                            {service.is_active ? 'الخدمة متاحة الآن' : 'الخدمة غير متاحة حالياً'}
                                                        </span>
                                                    </div>

                                                    {service.contact_phone && (
                                                        <div className="flex items-center gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                                                <PhoneCall className="w-4 h-4" />
                                                            </div>
                                                            <span dir="ltr" className="select-all font-medium text-right w-full">{service.contact_phone}</span>
                                                        </div>
                                                    )}

                                                    {(displayAddress || service.distance_km !== undefined) && (
                                                        <div className="flex items-start gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
                                                                <MapPin className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex-1">
                                                                {displayAddress && <span className="block line-clamp-1">{displayAddress}</span>}
                                                                {service.distance_km !== undefined && (
                                                                    <span className={`text-xs font-bold block ${displayAddress ? 'mt-1' : ''} ${service.distance_km < 10 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                                                        يبعد عنك: {service.distance_km < 1 ? 'أقل من 1 كم' : `${service.distance_km.toFixed(1)} كم`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {hasCoordinates && (
                                                        <div className="flex items-center gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                                                                <NavIcon className="w-4 h-4" />
                                                            </div>
                                                            <span dir="ltr" className="text-xs font-mono select-all">
                                                                {lat.toFixed(5)}, {lng.toFixed(5)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                                                    {mapsUrl && (
                                                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="col-span-2" onClick={(e) => e.stopPropagation()}>
                                                            <Button variant="outline" className="w-full rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-10 gap-2">
                                                                <Route className="w-4 h-4" /> عرض الموقع على الخريطة
                                                            </Button>
                                                        </a>
                                                    )}

                                                    {whatsappUrl && (
                                                        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="col-span-1" onClick={(e) => e.stopPropagation()}>
                                                            <Button className="w-full rounded-xl h-10 gap-2 bg-green-500 hover:bg-green-600 text-white">
                                                                <MessageCircle className="w-4 h-4" /> واتساب
                                                            </Button>
                                                        </a>
                                                    )}

                                                    <Link href="/chat" className={whatsappUrl ? 'col-span-1' : 'col-span-2'} onClick={(e) => e.stopPropagation()}>
                                                        <Button variant="outline" className="w-full rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50 h-10 gap-2">
                                                            <MessageSquare className="w-4 h-4" /> دردشة
                                                        </Button>
                                                    </Link>

                                                    {service.website_url && (
                                                        <a href={service.website_url} target="_blank" rel="noopener noreferrer" className="col-span-2" onClick={(e) => e.stopPropagation()}>
                                                            <Button variant="outline" className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 h-10 gap-2">
                                                                <ExternalLink className="w-4 h-4" /> زيارة الموقع
                                                            </Button>
                                                        </a>
                                                    )}

                                                    {isLocationService && (
                                                        <Button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedService(service);
                                                            }}
                                                            className="w-full rounded-xl h-10 gap-2 col-span-2 bg-primary hover:bg-primary/90 text-white"
                                                        >
                                                            <NavIcon className="w-4 h-4" /> طلب مساعدة
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* Assistance Request Modal */}
            <Dialog open={!!selectedService} onOpenChange={(open) => !open && setSelectedService(null)}>
                <DialogContent className="sm:max-w-md bg-white rounded-3xl border-0 shadow-2xl" dir="rtl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black text-slate-800 flex items-center gap-3">
                            <div className="p-2 bg-primary/10 text-primary rounded-xl">
                                <NavIcon className="w-5 h-5" />
                            </div>
                            طلب المساعدة
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 pt-2">
                            سيتم إرسال موقعك الحالي مع الطلب إلى <strong className="text-slate-800">{selectedService?.name}</strong> لتسهيل الوصول إليك.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        {!userLocation && (
                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 flex gap-3 text-amber-800 text-sm">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <p>موقعك غير محدد. ننصح بمشاركة موقعك (Location) لتسريع وصول مزود الخدمة، أو يمكنك وصف موقعك في الملاحظات.</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="notes" className="text-slate-700 font-bold">ملاحظات والتفاصيل</Label>
                            <Textarea
                                id="notes"
                                placeholder="اكتب أي معلومات إضافية (مثل: السيارة معطلة في المواقف السفلية، رقم اللوحة...)"
                                className="resize-none h-24 rounded-xl border-slate-200 focus-visible:ring-primary/20 focus-visible:border-primary bg-slate-50/50"
                                value={requestNotes}
                                onChange={(e) => setRequestNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-3 mt-2 sm:justify-start pt-2 border-t border-slate-100">
                        <Button
                            type="submit"
                            onClick={handleRequestAssistance}
                            disabled={submitting}
                            className="w-full sm:w-auto rounded-xl bg-primary hover:bg-primary/90 text-white h-12 px-8 font-bold text-base"
                        >
                            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : 'تأكيد إرسال الطلب'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedService(null)}
                            className="w-full sm:w-auto rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 h-12 px-6"
                        >
                            إلغاء
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
